import { useState } from 'react'
import { useRouter } from 'next/router'
import { useGetProductsByDisplayCollectionIdV3 } from '@/apis/rest/generated/hooks'
import { ProductSortByParamParameter, SortDirectionEnum } from '@/apis/rest/generated/types'
import { TrackerOrderByEnum } from '@/constants/tracker-sort-by-value.enum'
import useEventDetailEventTracker from '@/containers/event-tracker/event-detail.event-tracker'
import { decodeId } from '@/utils/utilApi'
import { ValueOf } from '@/utils/utilTs'

interface UseEventDetailSection {
  sectionIndex: number
  id: string
}

const EventDetailSectionSelectOrder = {
  mdRecommend: 'mdRecommend',
  highDiscountRate: 'highDiscountRate',
  highReviewCount: 'highReviewCount',
  productPromotionCustomerPriceAsc: 'productPromotionCustomerPriceAsc',
  productPromotionCustomerPriceDesc: 'productPromotionCustomerPriceDesc',
  reviewScore: 'reviewScore',
  created: 'created',
  salesScore: 'salesScore',
}

export type EventDetailSectionSelectOrderType = ValueOf<typeof EventDetailSectionSelectOrder>

interface SelectOptionsData {
  /** Query 요청시에 사용하는 데이터 */
  searchCondition: ProductSortByParamParameter
  /** Query 요청시에 사용하는 데이터 */
  direction: SortDirectionEnum
  /** 이벤트 트래커에서 사용하는 데이터 */
  trackerSortBy: TrackerOrderByEnum
}

/** @description searchCondition에서 품절상품에 대해서 우선정렬이 필요함 */
const selectOptionData: Record<EventDetailSectionSelectOrderType, SelectOptionsData> = {
  mdRecommend: {
    searchCondition: ProductSortByParamParameter.MD_RECOMMENDED,
    direction: SortDirectionEnum.ASC,
    trackerSortBy: TrackerOrderByEnum.MD,
  },
  highDiscountRate: {
    searchCondition: ProductSortByParamParameter.DISCOUNT_RATE,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.Discount,
  },
  highReviewCount: {
    searchCondition: ProductSortByParamParameter.REVIEW_COUNT,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.Review,
  },
  productPromotionCustomerPriceAsc: {
    searchCondition: ProductSortByParamParameter.SALE_PRICE,
    direction: SortDirectionEnum.ASC,
    trackerSortBy: TrackerOrderByEnum.LowPrice,
  },
  productPromotionCustomerPriceDesc: {
    searchCondition: ProductSortByParamParameter.SALE_PRICE,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.HighPrice,
  },
  reviewScore: {
    searchCondition: ProductSortByParamParameter.REVIEW_SCORE,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.ReviewScore,
  },
  salesScore: {
    searchCondition: ProductSortByParamParameter.SALES_SCORE,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.Popular,
  },
  created: {
    searchCondition: ProductSortByParamParameter.CREATED,
    direction: SortDirectionEnum.DESC,
    trackerSortBy: TrackerOrderByEnum.Recent,
  },
}

interface SelectOptionDisplay {
  value: EventDetailSectionSelectOrderType
  text: string
}

const selectOptionDisplay: SelectOptionDisplay[] = [
  {
    value: EventDetailSectionSelectOrder.productPromotionCustomerPriceAsc,
    text: '낮은 가격순',
  },
  {
    value: EventDetailSectionSelectOrder.salesScore,
    text: '인기 판매순',
  },
  {
    value: EventDetailSectionSelectOrder.reviewScore,
    text: '별점순',
  },
  {
    value: EventDetailSectionSelectOrder.highReviewCount,
    text: '리뷰 많은순',
  },
  {
    value: EventDetailSectionSelectOrder.highDiscountRate,
    text: '할인율 높은순',
  },
  {
    value: EventDetailSectionSelectOrder.mdRecommend,
    text: 'MD 추천순',
  },
  {
    value: EventDetailSectionSelectOrder.productPromotionCustomerPriceDesc,
    text: '높은 가격순',
  },
  {
    value: EventDetailSectionSelectOrder.created,
    text: '최신순',
  },
]

const defaultOptionIndex = 5

const useEventDetailSection = ({ sectionIndex, id }: UseEventDetailSection) => {
  const sectionOrderBy = `sob${sectionIndex}` // section-order-by로 섹션마다 orderBy를 저장
  const { query, pathname, replace } = useRouter()
  const { trackClickEventSaleProduct, trackClickEventsaleFilter } = useEventDetailEventTracker()
  const isIncludeSpecialDeal = ['100-won-deal'].includes(query.adSlug as string)
  const isSecret = query.isSecret === 'true'
  const { data: fetchedData, isLoading } = useGetProductsByDisplayCollectionIdV3(Number(decodeId(id).id), {
    sortBy:
      selectOptionData[selectOptionDisplay[Number(query?.[sectionOrderBy] || defaultOptionIndex)].value]
        .searchCondition,
    sortDirection:
      selectOptionData[selectOptionDisplay[Number(query?.[sectionOrderBy] || defaultOptionIndex)].value].direction,
    includeSpecialDeal: isIncludeSpecialDeal,
    sortSoldOutToLast: true,
    includeSoldOut: true,
    ...(isSecret && { exposeHiddenProduct: true }),
  })

  const [selectedOrderbyIndex, setSelectedOrderbyIndex] = useState(
    Number(query?.[sectionOrderBy]) || defaultOptionIndex
  )

  const updateUrlWithOrderBy = (params: Record<string, string>) => {
    const currentQueryParams = { ...query, ...params } as Record<string, string>
    const newUrl = `${pathname}?${new URLSearchParams(currentQueryParams).toString()}`
    replace(newUrl, undefined, { shallow: true })
  }

  const handleSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    // NetworkStatus가 setVariables으로 변경됨
    const orderByValue = e.target.value as EventDetailSectionSelectOrderType
    const idx = selectOptionDisplay.findIndex((item) => item.value === orderByValue)
    setSelectedOrderbyIndex(idx)
    updateUrlWithOrderBy({ [sectionOrderBy]: idx.toString() })
  }

  const selectedOptionValue = selectOptionDisplay[selectedOrderbyIndex].value

  return {
    selectedOptionValue,
    handleSelectChange,
    totalCount: fetchedData?.page?.totalCount || 0,
    fetchedData,
    isLoading,
    trackClickEventSaleProduct,
    trackClickEventsaleFilter,
    selectOptionDisplay,
    selectOptionData,
  }
}

export default useEventDetailSection
